import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  FaqAccordion,
  PageCta,
  PageHero,
  SectionHeading,
} from '../components/page-templates/SitePageTemplates';

const faqData = [
  {
    question: 'What does TrueLog do?',
    answer:
      'TrueLog is an integrated ICT logistics company built to manage complex, cross-border ICT operations end to end. We cover compliance, global transport, warehousing, fulfilment, and returns across 140+ countries.',
  },
  {
    question: 'What is an Importer of Record (IOR)?',
    answer:
      'An Importer of Record is the entity legally responsible for ensuring that imported goods comply with local laws and regulations. TrueLog acts as IOR across 140+ countries so customers can import ICT equipment without standing up a local entity in each market.',
  },
  {
    question: 'What countries does TrueLog cover?',
    answer:
      'TrueLog operates across 140+ countries worldwide, with a global network spanning Europe, Asia-Pacific, Africa, the Americas, and the Middle East plus offices in Singapore, Antwerp (Belgium), Chennai, Colombo, Dubai, and Ho Chi Minh City.',
  },
  {
    question: 'What types of companies does TrueLog serve?',
    answer:
      'We work with ICT asset management operators, MSPs, systems integrators, resellers, distributors, and other organisations that need to move, deploy, and manage ICT hardware globally without losing control.',
  },
  {
    question: 'What are TrueLog’s warehouse capabilities?',
    answer:
      'Our Changi Airport warehouse is a 12,000 sq ft WMS-enabled facility purpose-built for ICT equipment and high-value assets. We also operate through a standardised global warehousing model across certified partner facilities.',
  },
  {
    question: 'Does TrueLog provide on-site installation?',
    answer:
      'Yes. Our deployment and field services model includes destination warehousing, L1 engineer installation support, rack and stack services, white glove delivery, and reverse logistics with SLA options of 8, 12, or 24 hours.',
  },
  {
    question: 'What certifications does TrueLog hold?',
    answer:
      'TrueLog is IATA certified, a FIATA member, and carries USD 3M cargo insurance while operating through ISO-aligned process discipline across global operations.',
  },
];

const FAQ: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFAQs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return faqData;

    return faqData.filter(
      (faq) =>
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  return (
    <div className="bg-white">
      <PageHero
        eyebrow="FAQ"
        title="Operational answers for global ICT logistics teams."
        highlight="Operational answers"
        description="Use this page to understand how TrueLog approaches coverage, compliance, warehousing, deployment, and lifecycle control before you start a conversation with the team."
        heroIcon={QuestionMarkCircleIcon}
        accentIcon={ShieldCheckIcon}
        badges={[
          'Coverage and market entry',
          'Warehouse and fulfilment',
          'Deployment and lifecycle control',
        ]}
        metrics={[
          { value: '140+', label: 'Countries supported' },
          { value: '6', label: 'Global offices' },
          { value: '24/7', label: 'Control tower model' },
        ]}
        panelEyebrow="FAQ topics"
        panelTitle="The questions customers ask most often"
        panelDescription="These answers are focused on operational reality, not generic logistics definitions."
        panelItems={[
          {
            icon: ShieldCheckIcon,
            title: 'Compliance',
            description: 'How market entry, IOR, and customs responsibility are handled.',
          },
          {
            icon: QuestionMarkCircleIcon,
            title: 'Execution model',
            description: 'How services connect across movement, warehousing, preparation, and deployment.',
          },
          {
            icon: MagnifyingGlassIcon,
            title: 'Coverage',
            description: 'Where TrueLog operates and what global support actually looks like.',
          },
        ]}
      />

      <section className="relative z-20 -mt-16 px-4 pb-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl rounded-[30px] border border-white/80 bg-white/[0.9] p-6 shadow-[0_32px_100px_-46px_rgba(15,23,42,0.32)] backdrop-blur-2xl">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search questions or answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-2xl border border-slate-200/80 bg-slate-50 py-4 pl-12 pr-12 text-slate-900 outline-none transition-colors focus:border-cyan-300"
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
                aria-label="Clear search"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            ) : null}
          </div>
          <p className="mt-3 text-sm text-slate-500">
            {filteredFAQs.length} result{filteredFAQs.length === 1 ? '' : 's'} found
          </p>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="Questions"
            title="Browse the most relevant answers"
            description="If you do not see your exact scenario here, the operational specifics are usually the next thing to review with the team."
          />

          <div className="mx-auto max-w-4xl">
            {filteredFAQs.length ? (
              <FaqAccordion items={filteredFAQs} />
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-[30px] border border-slate-200/80 bg-white p-12 text-center shadow-[0_24px_60px_-40px_rgba(15,23,42,0.25)]"
              >
                <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-slate-300" />
                <h3 className="mt-4 text-2xl font-semibold text-slate-950">No matching answers</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">
                  Try different keywords, or reach out directly if the question is tied to a specific market, device type, or rollout condition.
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      <PageCta
        title="Need an answer tied to your actual operating environment?"
        description="If the question depends on your target countries, device type, compliance status, or rollout timing, we can walk through that with you directly."
        pills={['Coverage and compliance', 'Warehouse and deployment', 'Lifecycle and reporting']}
        secondaryHref="/resources"
        secondaryLabel="Explore resources"
      />
    </div>
  );
};

export default FAQ;
